import { createSelector } from "reselect"
import moment from "moment"
import {
  getCarrotLite,
  getCompanyHasIvfRequirements,
  getCompanyName,
  getHasAca,
  getIsAvaActive,
  getParentCompanyOrCompanyName
} from "reducers/companyInfo"
import {
  getLifetimeBenefitClaimed,
  getLifetimeMax,
  getPlanStartDate,
  getReimbursementPercent,
  getYearlyBenefitClaimed,
  getYearlyMax
} from "reducers/subsidyStatus"
import {
  getCardState,
  getDateOfBirth,
  getEligibilityDate,
  getFirstName,
  getHasAnUpcomingOrCompletedExpertChat,
  getHasBankInfoFilledOut,
  getHasEmployeePurchasedAva,
  getHasMetSignatureRequirement,
  getInterest,
  getIsLikelyToDoIvf,
  getIsUsa,
  getJourney,
  getLastName,
  getMemberCoverageType,
  getMemberHasHsa,
  getMemberHasLegalPartner,
  getMemberId,
  getMemberPlanSource,
  getPartnerCoverageType,
  getPartnerEmail,
  getPartnerFirstName,
  getPartnerHasHsa,
  getPartnerLastName,
  getPartnerPlanSource,
  getPlanStarted,
  getSelectedTradingPartnerId,
  getSex,
  getMemberHasSuccessfullySyncedInsurance,
  getEmailOptIn
} from "reducers/userInfo"
import Interests from "utils/Interests"
import CardStates from "utils/CardStates"
import {
  getCompanyHasCarrotSpecificDeductibleEnabled,
  getCompanyHasExternallyManagedDeductibleEnabled,
  getHasDeductible
} from "./deductibleSelectors"
import { getBenefitConfiguration } from "reducers/benefitConfiguration"

export const getEligibilityDateInFuture = createSelector([getEligibilityDate], (eligibilityDate) => {
  if (eligibilityDate) {
    return eligibilityDate > new moment()
  }
  return false
})

export const getIsNetflixMember = createSelector([getParentCompanyOrCompanyName], (parentCompanyOrCompanyName) => {
  return parentCompanyOrCompanyName.includes("Netflix")
})

export const getIsNBAMember = createSelector([getParentCompanyOrCompanyName], (parentCompanyOrCompanyName) => {
  return parentCompanyOrCompanyName === "NBA"
})

export const getIsWNBAMember = createSelector([getParentCompanyOrCompanyName], (parentCompanyOrCompanyName) => {
  return parentCompanyOrCompanyName === "WNBA"
})

export const getIsAvenuesMember = createSelector([getCompanyName], (companyName) => {
  return companyName === "Avenues USA"
})

export const getIsRomanHealthNYMandateMember = createSelector([getCompanyName], (companyName) => {
  return (
    companyName === "RHV" ||
    companyName === "Roman Health Ventures" ||
    companyName === "RHP" ||
    companyName === "Roman Health Pharmacy"
  )
})

export const getIsTestCompany = createSelector([getParentCompanyOrCompanyName], (parentCompanyOrCompanyName) => {
  return (
    parentCompanyOrCompanyName.startsWith("Test Company") ||
    parentCompanyOrCompanyName.includes("Baby Carrot") ||
    parentCompanyOrCompanyName.includes("Arun's Fruit Company")
  )
})

// currently we have a custom view for demo accounts of Latham and Watkins,
// which are set up in Stage DB only
export const getIsLathamAndWatkinsMember = createSelector(
  [getParentCompanyOrCompanyName],
  (parentCompanyOrCompanyName) => {
    return parentCompanyOrCompanyName.includes("Latham") && parentCompanyOrCompanyName.includes("Watkins")
  }
)

export const getIsCompanyUsingWINFertility = createSelector(
  [getParentCompanyOrCompanyName],
  (parentCompanyOrCompanyName) => {
    return parentCompanyOrCompanyName === "Orrick"
  }
)

export const getIsAvaVisible = createSelector(
  [getIsAvaActive, getInterest, getHasEmployeePurchasedAva],
  (isAvaActive, interest, hasEmployeePurchasedAva) => {
    return (
      isAvaActive &&
      interest !== Interests.ADOPTING &&
      interest !== Interests.GESTATIONAL_CARRIER &&
      !hasEmployeePurchasedAva
    )
  }
)

export const getHasPendingIvfRequirements = createSelector(
  [getCompanyHasIvfRequirements, getHasMetSignatureRequirement, getHasAnUpcomingOrCompletedExpertChat],
  (companyHasIvfRequirements, hasMetSignatureRequirement, hasAnUpcomingOrCompletedExpertChat) => {
    return companyHasIvfRequirements && (!hasMetSignatureRequirement || !hasAnUpcomingOrCompletedExpertChat)
  }
)

export const getHasPendingIvfRequirementsAndLikelyToDoIvf = createSelector(
  [
    getIsLikelyToDoIvf,
    getCompanyHasIvfRequirements,
    getHasMetSignatureRequirement,
    getHasAnUpcomingOrCompletedExpertChat
  ],
  (isLikelyToDoIvf, companyHasIvfRequirements, hasMetSignatureRequirement, hasAnUpcomingOrCompletedExpertChat) => {
    return (
      isLikelyToDoIvf &&
      companyHasIvfRequirements &&
      (!hasMetSignatureRequirement || !hasAnUpcomingOrCompletedExpertChat)
    )
  }
)

// currently we ignore startDate for carrot lite companies
// therefore we don't support carrot lite companies starting early
export const getBenefitStarted = createSelector(
  [getCarrotLite, getPlanStarted, getEligibilityDateInFuture],
  (carrotLite, planStarted, eligibilityDateInFuture) => {
    if (carrotLite) {
      return !eligibilityDateInFuture
    }
    return planStarted && !eligibilityDateInFuture
  }
)

export const getBenefitStartDate = createSelector(
  [getCarrotLite, getPlanStartDate, getEligibilityDate],
  (carrotLite, planStartDate, eligibilityDate) => {
    if (carrotLite) {
      return eligibilityDate
    }

    if (planStartDate > eligibilityDate) {
      return planStartDate
    }

    return eligibilityDate
  }
)

export const getMemberFullName = createSelector(
  [getFirstName, getLastName],
  (firstName, lastName) => `${firstName} ${lastName}`
)

export const getHasInterestAndJourney = createSelector(
  [getInterest, getJourney],
  (interest, journey) => interest && journey
)

export const getInterestIsFertility = createSelector([getInterest], (interest) => {
  return [
    Interests.UNDERSTAND_MY_FERTILITY,
    Interests.EMBRYO_FREEZING,
    Interests.EGG_FREEZING,
    Interests.SPERM_FREEZING,
    Interests.IVF,
    Interests.IUI,
    Interests.SOMETHING_ELSE
  ].includes(interest)
})

export const getHasPartnerInfo = createSelector(
  [getPartnerFirstName, getPartnerLastName, getPartnerEmail],
  (partnerFirstName, partnerLastName, partnerEmail) => partnerFirstName || partnerLastName || partnerEmail
)

export const getIsMemberOfCarrotCardCompany = createSelector(
  [getCardState],
  (cardState) => cardState !== CardStates.INELIGIBLE
)

export const getPercentageOfYearlyBenefitClaimed = createSelector(
  [getYearlyBenefitClaimed, getYearlyMax],
  (benefitClaimed, yearlyMax) => (benefitClaimed / yearlyMax) * 100
)

export const getPercentageOfLifetimeBenefitClaimed = createSelector(
  [getLifetimeBenefitClaimed, getLifetimeMax],
  (benefitClaimed, yearlyMax) => (benefitClaimed / yearlyMax) * 100
)

export const getIsBenefitLifetimeOnly = createSelector(
  [getLifetimeMax, getYearlyMax],
  (lifetimeMax, yearlyMax) => lifetimeMax === yearlyMax
)

export const getHasCostShare = createSelector(
  [getReimbursementPercent],
  (reimbursementPercent) => reimbursementPercent && reimbursementPercent !== 100
)

export const getHasVirtualCard = createSelector([getIsUsa], (isUsa) => !isUsa)

export const getHasRequestCardInformationPage = createSelector(
  [getIsNBAMember, getIsWNBAMember, getIsAvenuesMember, getIsRomanHealthNYMandateMember],
  (isNBAMember, isWNBAMember, isAvenuesMember, isRomanHealthNYMandateMember) =>
    isNBAMember || isWNBAMember || isAvenuesMember || isRomanHealthNYMandateMember
)

export const getCommonInsuranceFieldsCompleted = createSelector(
  [getMemberPlanSource, getMemberHasLegalPartner, getPartnerPlanSource],
  (memberPlanSource, memberHasLegalPartner, partnerPlanSource) => {
    const memberPlanSourceNonEmployerSponsoredPlanValue = "OTHER"
    const partnerPlanSourceNonEmployerSponsoredPlanValue = "OTHER"
    if (memberPlanSource === null || memberHasLegalPartner === null) {
      if (memberPlanSource === memberPlanSourceNonEmployerSponsoredPlanValue) {
        return true
      }
      return false
    }
    if (memberHasLegalPartner) {
      if (partnerPlanSource === partnerPlanSourceNonEmployerSponsoredPlanValue) {
        return true
      }
      if (partnerPlanSource === null) {
        return false
      }
    }
    return true
  }
)

export const getCsdSpecificFieldsCompleted = createSelector(
  [
    getMemberCoverageType,
    getMemberHasHsa,
    getMemberHasLegalPartner,
    getPartnerCoverageType,
    getPartnerHasHsa,
    getMemberPlanSource,
    getPartnerPlanSource
  ],
  (
    memberCoverageType,
    memberHasHsa,
    memberHasLegalPartner,
    partnerCoverageType,
    partnerHasHsa,
    memberPlanSource,
    partnerPlanSource
  ) => {
    const memberPlanSourceNonEmployerSponsoredPlanValue = "OTHER"
    if (memberCoverageType === null || memberHasHsa === null || memberHasLegalPartner === null) {
      if (memberPlanSource === memberPlanSourceNonEmployerSponsoredPlanValue) {
        return true
      }
      return false
    }

    if (memberHasLegalPartner) {
      if (partnerPlanSource === memberPlanSourceNonEmployerSponsoredPlanValue) {
        return true
      }
      if (partnerCoverageType === null || partnerHasHsa === null) {
        return false
      }
    }

    return true
  }
)

export const getTellUsInsuranceStepCompleted = createSelector(
  [
    getHasAca,
    getCompanyHasCarrotSpecificDeductibleEnabled,
    getCompanyHasExternallyManagedDeductibleEnabled,
    getCommonInsuranceFieldsCompleted,
    getCsdSpecificFieldsCompleted
  ],
  (
    hasAca,
    companyHasCarrotSpecificDeductibleEnabled,
    companyHasExternalDeductibleEnabled,
    commonInsuranceFieldsCompleted,
    csdSpecificFieldsCompleted
  ) => {
    if (hasAca || companyHasCarrotSpecificDeductibleEnabled || companyHasExternalDeductibleEnabled) {
      if (!commonInsuranceFieldsCompleted) {
        return false
      }
    }

    if (companyHasCarrotSpecificDeductibleEnabled) {
      if (!csdSpecificFieldsCompleted) {
        return false
      }
    }

    return true
  }
)

export const getConnectInsuranceStepCompleted = createSelector(
  [getDateOfBirth, getSex, getSelectedTradingPartnerId, getMemberId],
  (dateOfBirth, sex, selectedTradingPartnerId, memberId) => {
    return dateOfBirth != null && sex != null && memberId != null && selectedTradingPartnerId != null
  }
)

export const getNeedsBenefitEnrollment = createSelector(
  [getCompanyHasExternallyManagedDeductibleEnabled, getTellUsInsuranceStepCompleted, getConnectInsuranceStepCompleted],
  (companyHasExternallyManagedDeductibleEnabled, tellUsInsuranceStepCompleted, connectInsuranceStepCompleted) => {
    if (!tellUsInsuranceStepCompleted) {
      return true
    }

    return companyHasExternallyManagedDeductibleEnabled && !connectInsuranceStepCompleted
  }
)

export const getConnectInsuranceInProgress = createSelector(
  [getConnectInsuranceStepCompleted, getTellUsInsuranceStepCompleted],
  (connectInsuranceStepCompleted, tellUsInsuranceStepCompleted) => {
    return !connectInsuranceStepCompleted && tellUsInsuranceStepCompleted
  }
)

export const getShouldShowOptionalInsuranceInfoPromo = createSelector(
  [getCompanyHasCarrotSpecificDeductibleEnabled, getHasDeductible, getMemberHasSuccessfullySyncedInsurance],
  (companyHasCarrotSpecificDeductibleEnabled, hasDeductible, memberHasSuccessfullySyncedInsurance) => {
    return !memberHasSuccessfullySyncedInsurance && companyHasCarrotSpecificDeductibleEnabled && hasDeductible
  }
)

export const getShouldFillOutBankInfo = createSelector(
  [getHasBankInfoFilledOut, getBenefitConfiguration],
  (hasBankInfoFilledOut, benefitConfiguration) =>
    !hasBankInfoFilledOut && benefitConfiguration.featureConfig.isBankInfoEnabled
)
